/**
 * Shared UI helper functions for admin components.
 *
 * Contains common formatting and rendering utilities used across
 * reconciliation admin views.
 */

/**
 * Render a status icon based on the status string.
 *
 * @param {string} status - The status to render ('active', 'complete', 'pending', 'processing', or error)
 * @returns {JSX.Element} A colored status indicator span
 */
export const getStatusIcon = (status) => {
  if (status === 'active' || status === 'complete') {
    return { icon: '✓', className: 'text-green-500' }
  } else if (status === 'pending' || status === 'processing') {
    return { icon: '⟳', className: 'text-yellow-500' }
  } else {
    return { icon: '✗', className: 'text-red-500' }
  }
}

/**
 * Get a color class based on status.
 *
 * @param {string} status - The status string
 * @returns {string} A Tailwind CSS color class
 */
export const getStatusColor = (status) => {
  if (status === 'active' || status === 'complete') {
    return 'text-green-500'
  } else if (status === 'pending' || status === 'processing') {
    return 'text-yellow-500'
  } else {
    return 'text-red-500'
  }
}

/**
 * Format an ISO date string for display.
 *
 * @param {string} isoDate - ISO 8601 date string
 * @returns {string} Localized date string or empty string if no date provided
 */
export const formatDate = (isoDate) => {
  if (!isoDate) return ''
  const date = new Date(isoDate)
  return date.toLocaleString()
}

/**
 * Format a date as relative time (e.g., "2 hours ago").
 *
 * @param {string} isoDate - ISO 8601 date string
 * @returns {string} Relative time string
 */
export const formatRelativeDate = (isoDate) => {
  if (!isoDate) return ''

  const date = new Date(isoDate)
  const now = new Date()
  const diffMs = now - date
  const diffSecs = Math.floor(diffMs / 1000)
  const diffMins = Math.floor(diffSecs / 60)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffSecs < 60) {
    return 'just now'
  } else if (diffMins < 60) {
    return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`
  } else if (diffDays < 7) {
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`
  } else {
    return date.toLocaleDateString()
  }
}

/**
 * Format artists array for display.
 *
 * @param {Array} artists - Array of artist names or artist objects with name property
 * @returns {string} Comma-separated artist names
 */
export const formatArtists = (artists) => {
  if (!artists || artists.length === 0) return 'Unknown Artist'

  return artists.map((artist) => (typeof artist === 'string' ? artist : artist.name)).join(', ')
}

/**
 * Truncate text to a maximum length with ellipsis.
 *
 * @param {string} text - The text to truncate
 * @param {number} maxLength - Maximum length before truncation
 * @returns {string} Truncated text with ellipsis if needed
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text || text.length <= maxLength) return text || ''
  return text.substring(0, maxLength - 3) + '...'
}

/**
 * Get the start of the week (Sunday) for a given date.
 *
 * @param {Date|string} date - Date object or ISO string
 * @returns {Date} Date object set to start of week (Sunday, midnight)
 */
export const getWeekStart = (date) => {
  const d = new Date(date)
  const day = d.getDay()
  const diff = d.getDate() - day
  const weekStart = new Date(d)
  weekStart.setDate(diff)
  weekStart.setHours(0, 0, 0, 0)
  return weekStart
}

/**
 * Format a week label for display (e.g., "Jan 13, 2026").
 *
 * @param {Date} weekStart - Start of the week
 * @returns {string} Formatted week label
 */
export const formatWeekLabel = (weekStart) => {
  const now = new Date()
  const thisWeekStart = getWeekStart(now)
  const lastWeekStart = new Date(thisWeekStart)
  lastWeekStart.setDate(lastWeekStart.getDate() - 7)

  // Check if it's this week or last week
  if (weekStart.getTime() === thisWeekStart.getTime()) {
    return 'This week'
  } else if (weekStart.getTime() === lastWeekStart.getTime()) {
    return 'Last week'
  }

  // Otherwise show "Mon D, YYYY" format
  const month = weekStart.toLocaleDateString('en-US', { month: 'short' })
  const day = weekStart.getDate()
  const year = weekStart.getFullYear()
  return `${month} ${day}, ${year}`
}
